const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/pages/InvoicePage/InvoicePDFDialog.tsx';
import { useTheme } from '@mui/material/styles';
import { css, cx } from '@emotion/css';
import { Dialog, DialogContent, DialogTitle, IconButton, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useMemo } from 'react';
import { Report } from '@cistec/common_web/defs/kisim';
import { PDFDocumentController } from '@cistec/common_web/components/PDFViewer/PDFDocumentController';
import { PDFViewerProvider } from '@cistec/common_web/components/PDFViewer/PDFViewerProvider';
import { PDFDocument } from '@cistec/common_web/components/PDFViewer/PDFDocument';

import { usePDF } from 'App/store/documents';

interface Props {
    invoice: Report;
    onClose: () => void;
}

function useCSSClasses() {
    const theme = useTheme();
    return useMemo(
        () => ({
            // little workaround to get the dialog to have a fixed distance from top and bottom
            dialog: css`
                *[role='dialog'] {
                    position: absolute;
                    top: 20px;
                    bottom: 20px;
                    margin: 0;
                }
            `,
            dialogTitle: css`
                display: flex;
                align-items: center;
                justify-content: space-between;
                height: ${theme.spacing(7)};
                padding: 0 ${theme.spacing(2)};

                & > .documentTitle {
                    flex: none;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    vertical-align: middle;

                    ${theme.breakpoints.down('lg')} {
                        display: none;
                    }
                }

                & > *:last-child {
                    // icon button
                    flex: none;
                    padding: 0;
                    margin-left: ${theme.spacing(2)};
                }
            `,
            dialogContent: css`
                position: relative;
                padding: 0;

                & > div {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    overflow: hidden;
                }
            `,
            pdfViewer: css`
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                display: flex;
                flex-direction: column;
                border-top: 1px solid ${theme.palette.grey['500']};
            `,
            pdfDocument: css`
                flex: auto;
            `,
            bottomBar: css`
                display: flex;
                justify-content: flex-end;
                align-items: center;
                height: ${theme.spacing(7)};
                flex: none;
                padding: 0 ${theme.spacing(2)};
                border-top: 1px solid #e2e2e2;
            `,
        }),
        [theme]
    );
}

export const InvoicePDFDialog = (props: Props) => {
    const theme = useTheme();
    const breakpointLarge = useMediaQuery(theme.breakpoints.down('lg'));
    const classes = useCSSClasses();
    const { invoice } = props;

    const handleDialogClose = () => {
        props.onClose();
    };

    const { data: invoicePDF } = usePDF('PatReports', invoice.REPORTNR, invoice.SENDER);

    const text = invoice.NAME;

    return invoicePDF ? (
        <PDFViewerProvider documentTitle={text}>
            <Dialog
                open={true}
                onClose={handleDialogClose}
                fullScreen={breakpointLarge}
                fullWidth={!breakpointLarge}
                maxWidth={false}
                className={cx({ [classes.dialog]: !breakpointLarge })}
            >
                <DialogTitle className={classes.dialogTitle}>
                    <div className="documentTitle">{text}</div>
                    <PDFDocumentController />
                    <IconButton color="inherit" onClick={handleDialogClose}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <div className={cx('PDFViewer', classes.pdfViewer)}>
                        <PDFDocument className={classes.pdfDocument} pdfASCIIString={invoicePDF} />
                    </div>
                </DialogContent>
            </Dialog>
        </PDFViewerProvider>
    ) : null;
};
